import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {WordList} from '../../domain/types';
import {
  fetchUserWordlists,
  fetchMarketplaceWordlists,
  fetchWordlistById,
  createWordlist,
  updateWordlist,
  deleteWordlist,
  fetchUserWordlistsCollection,
  addWordlistToCollection,
  removeWordlistFromCollection,
} from './wordlistsThunks';

export type ThunkStatus = 'idle' | 'pending' | 'success' | 'rejected';

interface ApiState<T> {
    status: ThunkStatus;
    data: T;
}

interface WordlistsState {
    // User's own wordlists (authenticated) - wordlists created by the user
    userWordlists: ApiState<WordList[]>;

    // User's wordlist collection - wordlists the user has added (owned or public)
    userWordlistsCollection: ApiState<WordList[]>;

    // Public marketplace wordlists (no auth required)
    marketplaceWordlists: ApiState<WordList[]>;

    // Currently selected/detailed wordlist
    wordlistDetail: ApiState<WordList | null>;

    // Selected wordlist ID
    selectedWordlistId: string | null;

    // Error messages
    error: string | null;
}

const initialState: WordlistsState = {
  userWordlists: {
    status: 'idle',
    data: [],
  },
  userWordlistsCollection: {
    status: 'idle',
    data: [],
  },
  marketplaceWordlists: {
    status: 'idle',
    data: [],
  },
  wordlistDetail: {
    status: 'idle',
    data: null,
  },
  selectedWordlistId: null,
  error: null,
};

const wordlistsSlice = createSlice({
  name: 'wordlists',
  initialState,
  reducers: {
    setSelectedWordlistId: (state, action: PayloadAction<string | null>) => {
      state.selectedWordlistId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearWordlistDetail: (state) => {
      state.wordlistDetail = {
        status: 'idle',
        data: null,
      };
    },
  },
  extraReducers: (builder) => {
        // Fetch User Wordlists
    builder.addCase(fetchUserWordlists.pending, (state) => {
      state.userWordlists.status = 'pending';
      state.error = null;
    });
    builder.addCase(fetchUserWordlists.fulfilled, (state, action) => {
      state.userWordlists.status = 'success';
      state.userWordlists.data = action.payload;
      state.error = null;
    });
    builder.addCase(fetchUserWordlists.rejected, (state, action) => {
      state.userWordlists.status = 'rejected';
      state.error = action.payload as string;
    });

        // Fetch Marketplace Wordlists
    builder.addCase(fetchMarketplaceWordlists.pending, (state) => {
      state.marketplaceWordlists.status = 'pending';
      state.error = null;
    });
    builder.addCase(fetchMarketplaceWordlists.fulfilled, (state, action) => {
      state.marketplaceWordlists.status = 'success';
      state.marketplaceWordlists.data = action.payload;
      state.error = null;
    });
    builder.addCase(fetchMarketplaceWordlists.rejected, (state, action) => {
      state.marketplaceWordlists.status = 'rejected';
      state.error = action.payload as string;
    });

        // Fetch Wordlist By ID
    builder.addCase(fetchWordlistById.pending, (state) => {
      state.wordlistDetail.status = 'pending';
      state.error = null;
    });
    builder.addCase(fetchWordlistById.fulfilled, (state, action) => {
      state.wordlistDetail.status = 'success';
      state.wordlistDetail.data = action.payload;
      state.error = null;
    });
    builder.addCase(fetchWordlistById.rejected, (state, action) => {
      state.wordlistDetail.status = 'rejected';
      state.error = action.payload as string;
    });

        // Create Wordlist
    builder.addCase(createWordlist.pending, (state) => {
      state.error = null;
    });
    builder.addCase(createWordlist.fulfilled, (state, action) => {
            // Add to user wordlists
      state.userWordlists.data.push(action.payload);
            // If it's public, also add to marketplace
      if (action.payload.isPublic) {
        state.marketplaceWordlists.data.push(action.payload);
      }
      state.error = null;
    });
    builder.addCase(createWordlist.rejected, (state, action) => {
      state.error = action.payload as string;
    });

        // Update Wordlist
    builder.addCase(updateWordlist.pending, (state) => {
      state.error = null;
    });
    builder.addCase(updateWordlist.fulfilled, (state, action) => {
            // Update in user wordlists
      const userIndex = state.userWordlists.data.findIndex(w => w.id === action.payload.id);
      if (userIndex !== -1) {
        state.userWordlists.data[userIndex] = action.payload;
      }

            // Update in marketplace if it's public
      const marketIndex = state.marketplaceWordlists.data.findIndex(w => w.id === action.payload.id);
      if (action.payload.isPublic) {
        if (marketIndex !== -1) {
          state.marketplaceWordlists.data[marketIndex] = action.payload;
        } else {
          state.marketplaceWordlists.data.push(action.payload);
        }
      } else if (marketIndex !== -1) {
                // Remove from marketplace if it's no longer public
        state.marketplaceWordlists.data.splice(marketIndex, 1);
      }

            // Update detail if it's the selected one
      if (state.wordlistDetail.data?.id === action.payload.id) {
        state.wordlistDetail.data = action.payload;
      }

      state.error = null;
    });
    builder.addCase(updateWordlist.rejected, (state, action) => {
      state.error = action.payload as string;
    });

        // Delete Wordlist
    builder.addCase(deleteWordlist.pending, (state) => {
      state.error = null;
    });
    builder.addCase(deleteWordlist.fulfilled, (state, action) => {
      const deletedId = action.payload;

            // Remove from user wordlists
      state.userWordlists.data = state.userWordlists.data.filter(w => w.id !== deletedId);

            // Remove from user collection
      state.userWordlistsCollection.data = state.userWordlistsCollection.data.filter(w => w.id !== deletedId);

            // Remove from marketplace
      state.marketplaceWordlists.data = state.marketplaceWordlists.data.filter(w => w.id !== deletedId);

            // Clear detail if it's the deleted one
      if (state.wordlistDetail.data?.id === deletedId) {
        state.wordlistDetail.data = null;
        state.selectedWordlistId = null;
      }

      state.error = null;
    });
    builder.addCase(deleteWordlist.rejected, (state, action) => {
      state.error = action.payload as string;
    });

        // Fetch User Wordlists Collection
    builder.addCase(fetchUserWordlistsCollection.pending, (state) => {
      state.userWordlistsCollection.status = 'pending';
      state.error = null;
    });
    builder.addCase(fetchUserWordlistsCollection.fulfilled, (state, action) => {
      state.userWordlistsCollection.status = 'success';
      state.userWordlistsCollection.data = action.payload;
      state.error = null;
    });
    builder.addCase(fetchUserWordlistsCollection.rejected, (state, action) => {
      state.userWordlistsCollection.status = 'rejected';
      state.error = action.payload as string;
    });

        // Add Wordlist to Collection
    builder.addCase(addWordlistToCollection.pending, (state) => {
      state.error = null;
    });
    builder.addCase(addWordlistToCollection.fulfilled, (state, action) => {
            // Add to collection if not already there
      const exists = state.userWordlistsCollection.data.some(w => w.id === action.payload.id);
      if (!exists) {
        state.userWordlistsCollection.data.push(action.payload);
      }
      state.error = null;
    });
    builder.addCase(addWordlistToCollection.rejected, (state, action) => {
      state.error = action.payload as string;
    });

        // Remove Wordlist from Collection
    builder.addCase(removeWordlistFromCollection.pending, (state) => {
      state.error = null;
    });
    builder.addCase(removeWordlistFromCollection.fulfilled, (state, action) => {
      const removedId = action.payload;
            // Remove from collection
      state.userWordlistsCollection.data = state.userWordlistsCollection.data.filter(w => w.id !== removedId);
      state.error = null;
    });
    builder.addCase(removeWordlistFromCollection.rejected, (state, action) => {
      state.error = action.payload as string;
    });
  },
});

export const {
  setSelectedWordlistId,
  clearError,
  clearWordlistDetail,
} = wordlistsSlice.actions;

export default wordlistsSlice.reducer;
